/**
 * RecordPaymentDialog — apply a payment to one open bill.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { useAP } from "@/data/accounting/ap/APContext";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import type { Bill, BillPaymentMethod } from "@/data/accounting/ap/apTypes";

const today = () => new Date().toISOString().slice(0, 10);

export function RecordPaymentDialog({ bill, onOpenChange }: {
  bill: Bill | null;
  onOpenChange: (o: boolean) => void;
}) {
  const { recordPayment } = useAP();
  const { accounts } = useGeneralLedger();
  const [paidAt, setPaidAt] = useState(today());
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<BillPaymentMethod>("ach");
  const [fromAccountId, setFromAccountId] = useState("1000");
  const [reference, setReference] = useState("");

  useEffect(() => {
    if (bill) {
      const open = bill.total - bill.amountPaid;
      setAmount(open.toFixed(2));
      setPaidAt(today());
      setReference("");
      setMethod("ach");
      setFromAccountId("1000");
    }
  }, [bill]);

  const payFromAccounts = accounts.filter(a =>
    a.active && (a.subtype === "cash" || a.subtype === "credit_card_payable"));

  const submit = () => {
    if (!bill) return;
    const amt = parseFloat(amount);
    if (Number.isNaN(amt) || amt <= 0) {
      return toast({ title: "Amount must be positive", variant: "destructive" });
    }
    try {
      recordPayment({
        billId: bill.id, paidAt, amount: amt, method, fromAccountId, reference,
      });
      toast({ title: "Payment posted", description: `$${amt.toFixed(2)} to ${bill.vendorName}` });
      onOpenChange(false);
    } catch (e) {
      toast({ title: "Couldn't record payment", description: (e as Error).message, variant: "destructive" });
    }
  };

  return (
    <Dialog open={!!bill} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
        </DialogHeader>
        {bill && (
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              {bill.vendorName} · {bill.invoiceNumber} · Open ${(bill.total - bill.amountPaid).toFixed(2)}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Paid date</Label>
                <Input type="date" value={paidAt} onChange={e => setPaidAt(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Amount</Label>
                <Input value={amount} inputMode="decimal" className="tabular-nums text-right"
                  onChange={e => setAmount(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Method</Label>
                <Select value={method} onValueChange={v => setMethod(v as BillPaymentMethod)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ach">ACH</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">From account</Label>
                <Select value={fromAccountId} onValueChange={setFromAccountId}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {payFromAccounts.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.number} · {a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Reference (check #, ACH id…)</Label>
                <Input value={reference} onChange={e => setReference(e.target.value)} />
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit}>Post Payment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
